import { APIBaseResponse } from "../api";

export interface OrganizationDetails {
    org_name: string;
    default_timezone?: string;
    meta_data?: Record<string, unknown>;
}

/** Tenant metadata as returned by GET /api/v2/tenant/metadata */
export interface TenantMetadataData {
    widget_icon_url: string;
    font_url: string;
    font_color: string;
    pal_header_bg?: string;
    pal_text_color?: string;
    timezone?: string;
}

export interface GetTenantMetadataAPIResponse extends APIBaseResponse {
    data: TenantMetadataData;
}

/** Payload for PATCH /api/v2/tenant/metadata (all fields optional; partial update supported) */
export interface TenantMetadataPatchPayload {
    widget_icon_url?: string;
    font_url?: string;
    font_color?: string;
    pal_header_bg?: string;
    pal_text_color?: string;
    timezone?: string;
}

/** Response body from PATCH /api/v2/tenant/metadata (data only, no status_code/message wrapper in typical usage) */
export interface TenantMetadataPatchResponse {
    widget_icon_url: string;
    font_url: string;
    font_color: string;
    pal_header_bg?: string;
    pal_text_color?: string;
    timezone?: string;
}

export interface TenantTimezone {
    name: string;
    abbrev: string;
}

export interface GetTenantTimezonesAPIResponse extends APIBaseResponse {
    data?: {
        timezones?: TenantTimezone[];
    };
}

/** Request for POST /api/v2/tenant/metadata/upload - get presigned URL and permanent widget_icon_url */
export interface TenantMetadataUploadRequest {
    filename: string;
}

/** Response from POST /api/v2/tenant/metadata/upload */
export interface TenantMetadataUploadResponse {
    presigned_url: string;
    widget_icon_url: string;
}

export interface GetTenantMetadataUploadAPIResponse extends APIBaseResponse {
    data: TenantMetadataUploadResponse;
}

export interface GetDetailsAPIResponse extends APIBaseResponse {
    data: OrganizationDetails;
}

interface TeamMember {
    email: string;
    name: string | null;
    role: string | null;
    user_id: string;
    roles: string[];
    status: string;
}

export interface SettingsTeamsAPIResponse extends APIBaseResponse {
    data: TeamMember[];
}

export interface AdvisorRegionLanguage {
    code: string;
    name: string;
}

export interface AdvisorRegionSetting {
    country_code: string;
    country_name: string;
    languages: AdvisorRegionLanguage[];
}

interface AbilityMetadata {
    format?: string;
    language?: string;
    selected_languages?: string[];
    selected_voice_agent?: string;
    regions?: AdvisorRegionSetting[];
}


export enum SETTING_TYPE {
    DEPTH = 'depth',
    COT = 'CoT',
    AUTO_TRAIN = 'auto_train',
    SPEAK = 'voice',
    TRANSLATION = 'translation',
    ZAPIER = 'zapier',
    EXPLAIN_ANSWER = 'explain_answer',
    IMAGES = 'images',
    SCHEMATIC_DIAGRAMS = 'schematic_diagrams',
    VIDEO = 'video',
    AUDIO = 'audio',
    TEMPERATURE = 'temperature',
    LIVE_CHAT = 'livechat',
    FILE_INPUT = 'file_input',
    COUNTRY = 'country',
    DARK_MODE = 'dark_mode',
    RUM = 'rum',
}
export interface Setting {
    ability: SETTING_TYPE;
    is_active: boolean;
    metadata: AbilityMetadata;
    description: string;
    value?: number;
    ability_value?: number;
}

export interface AdvisorAbility extends Setting {
    advisor_id: string;
}

export interface SettingsData {
    settings: Setting[];
    advisors: AdvisorAbility[];
}

export interface SettingsAPIResponse extends APIBaseResponse {
    data: SettingsData;
}

// Scheduler Types
export type ScheduleState = 'ENABLED' | 'DISABLED';
export type ScheduleType = 'Rate' | 'Recurring' | 'One-time' | 'Immediate';
export type RateUnit = 'minutes' | 'hours' | 'days';
export type TargetType = 'step_function' | 'lambda' | 'agent' | 'fabric';

export interface ScheduleRate {
    value: number;
    unit: RateUnit;
}

export interface ScheduleCron {
    minutes: string;
    hours: string;
    day_of_month: string;
    month: string;
    day_of_week: string;
    year: string;
}

export interface ExpressionMetadata {
    type: ScheduleType;
    schedule_rate?: ScheduleRate;
    schedule_cron?: ScheduleCron;
    schedule_at?: string;
}

export interface Schedule {
    scheduler_id: string;
    schedule_name: string;
    group_name?: string;
    state: ScheduleState;
    simplified_expression?: string;
    schedule_type: ScheduleType;
    expression_metadata?: ExpressionMetadata;
    // API may return these at top level or nested in expression_metadata
    schedule_rate?: ScheduleRate;
    schedule_cron?: ScheduleCron;
    schedule_at?: string;
    target_id: string;
    target_type?: TargetType;
    timezone?: string;
    schedule_timezone?: string;
    description?: string;
    created_at?: string;
    updated_at?: string;
    meta_data?: { custom_params_key_type?: 'key_pair' | 'object'; [key: string]: unknown };
}

export interface ScheduleListData {
    message: string;
    count: number;
    items: Schedule[];
}

export interface ScheduleListResponse extends APIBaseResponse {
    data: ScheduleListData;
}

// Scheduler history (activity log) - list of batches for a schedule
export interface SchedulerHistoryBatchItem {
    batch_id: string;
    scheduler_id: string;
    target_type?: string;
    no_of_received_claims?: string;
    no_of_processed_claims?: string;
    no_of_skipped_claims?: string;
    no_of_retried_claims?: string;
    status: string;
    created_at: string;
    input?: Record<string, unknown>;
}

export interface SchedulerHistoryResponse {
    total_count: number;
    page: number;
    per_page: number;
    scheduler_id: string;
    data: SchedulerHistoryBatchItem[];
}

// Batch detail (expanded accordion) - single batch with summary
export interface BatchSummaryItem {
    is_skipped: boolean;
    claimnumber: string;
    is_processed: boolean;
    aiworker_usage_id: string | null;
    reason?: string;
}

export interface SchedulerBatchDetailResponse {
    batch_id: string;
    scheduler_id: string;
    target_type?: string;
    no_of_received_claims?: string;
    no_of_processed_claims?: string;
    no_of_skipped_claims?: string;
    no_of_retried_claims?: string;
    received_claims?: string[];
    skipped_claims?: string[];
    processed_claims?: string[];
    batch_summary?: { summary: BatchSummaryItem[]; retry_summary?: BatchSummaryItem[] };
    status: string;
    created_at: string;
    updated_at?: string;
    input?: Record<string, unknown>;
    output?: Record<string, unknown>;
}

export interface ScheduleResponse extends APIBaseResponse {
    data: Schedule;
}

export interface AuditLogItem {
    id?: number;
    audit_id: string;
    tenant_id?: string;
    audit_type: string;
    module: string;
    sub_module: string | null;
    resource_id: string | null;
    resource_name: string | null;
    description: string;
    status: string;
    executed_by: string;
    user_id: string;
    executed_at: string;
}

export interface AuditLogMessageIdItem {
    user_input: string;
    message_id: string;
}

export interface AuditLogFailedItem {
    user_input: string;
    error: string;
}

export interface AuditLogDeltaItem {
    path: string;
    old: unknown;
    new: unknown;
}

export type AuditLogFilterCondition =
    | 'equals'
    | 'does_not_equal'
    | 'contains'
    | 'does_not_contain'
    | 'begins_with'
    | 'ends_with'
    | 'gt'
    | 'gte'
    | 'lt'
    | 'lte'
    | 'in'
    | 'not_in';

/** Scalar filter field value for audit logs history API. */
export type AuditLogFilterValue = string | number;

export interface AuditLogMetadataFilterEntry {
    key: string;
    value: string;
    condition: AuditLogFilterCondition;
}

/**
 * Filter entry for audit logs history API, e.g. `{ module: 'event_scheduler', condition: 'equals' }`
 * or `{ metadata: [{ key: 'advisor_id', value: '...', condition: 'equals' }] }`.
 */
export interface AuditLogApiFilter {
    condition?: AuditLogFilterCondition;
    metadata?: AuditLogMetadataFilterEntry[];
    [field: string]: AuditLogFilterValue | string[] | AuditLogMetadataFilterEntry[] | AuditLogFilterCondition | undefined;
}

export interface AuditLogPagination {
    page: number;
    per_page: number;
}

export interface AuditLogSorting {
    field: string;
    order: 'asc' | 'desc';
}

export interface AuditLogsHistoryRequest {
    filters: AuditLogApiFilter[];
    pagination: AuditLogPagination;
    sorting?: AuditLogSorting;
}

export interface AuditLogsAvailableFilters {
    audit_type?: string[];
    status?: string[];
    module?: string[];
    module_sub_module_map?: Record<string, string[]>;
}

export interface AuditLogsData {
    total_count: number;
    page: number;
    per_page: number;
    data: AuditLogItem[];
    applied_filters?: Record<string, unknown>;
    available_filters?: AuditLogsAvailableFilters;
}

export interface AuditLogsAPIResponse extends APIBaseResponse {
    data: AuditLogsData;
}

export interface AuditLogMetadata {
    request_id?: string;
    mode?: string;
    aiworker_id?: string;
    aiworker_name?: string;
    target_id?: string | null;
    sql_query?: string;
    claim_column?: string;
    reason?: string;
    row_count?: number;
    distinct_claim_count?: number;
    successful_count?: number;
    failed_count?: number;
    skipped_count?: number;
    claim_numbers?: string[];
    message_ids?: AuditLogMessageIdItem[];
    failed_items?: AuditLogFailedItem[];
    skipped_rows?: unknown[];
    duration_ms?: number;
    [key: string]: unknown;
}

export interface AuditLogReprocessSummary {
    row_count?: number;
    distinct_claim_count?: number;
    successful_count?: number;
    failed_count?: number;
    skipped_count?: number;
}

export interface AuditLogDetail extends AuditLogItem {
    old_data?: Record<string, unknown> | null;
    new_data?: Record<string, unknown> | null;
    delta_data?: AuditLogDeltaItem[] | null;
    metadata?: AuditLogMetadata | null;
    /** Summary counts may be returned at root or under reprocess_summary. */
    reprocess_summary?: AuditLogReprocessSummary;
    row_count?: number;
    distinct_claim_count?: number;
    successful_count?: number;
    failed_count?: number;
    skipped_count?: number;
}

export interface AuditLogDetailAPIResponse extends APIBaseResponse {
    data: AuditLogDetail;
}

export type ReprocessUsageStatus = 'failed' | 'succeeded' | 'in_progress' | 'all';
export type ReprocessDateMode = 'relative' | 'range';

export interface ReprocessClaimFilter {
    usage_status: ReprocessUsageStatus;
    date_mode: ReprocessDateMode;
    days?: number;
    from?: string;
    to?: string;
    limit?: number;
}

export interface ReprocessClaimsPayload {
    source: 'ui';
    trigger_agent: boolean;
    reason: string;
    aiworker_id: string;
    credential_id?: string;
    claim_filter: ReprocessClaimFilter;
}

export interface ReprocessPreviewResultData {
    message: string;
    claim_numbers: string[];
    row_count: number;
    distinct_claim_count: number;
    audit_id: string;
}

export interface ReprocessTriggerResultData {
    message: string;
    audit_id: string;
    total_items: number;
    successful_count: number;
    failed_count: number;
    skipped_count: number;
    skipped_rows: unknown[];
    claim_numbers: string[];
}

export interface ReprocessClaimsAPIResponse extends APIBaseResponse {
    data: ReprocessPreviewResultData | ReprocessTriggerResultData;
}

export interface CreateSchedulePayload {
    schedule_name: string;
    target_id: string;
    target_type: TargetType;
    schedule_type: ScheduleType;
    schedule_rate?: ScheduleRate;
    schedule_cron?: ScheduleCron;
    schedule_at?: string;
    schedule_expression?: string; // Alternative to structured fields
    description?: string;
    state: ScheduleState;
    schedule_timezone?: string;
}

export interface UpdateSchedulePayload {
    schedule_type?: ScheduleType;
    schedule_rate?: ScheduleRate;
    schedule_cron?: ScheduleCron;
    schedule_at?: string;
    schedule_expression?: string;
    target_id?: string;
    target_type?: TargetType;
    state?: ScheduleState;
    schedule_timezone?: string;
    description?: string;
}

export interface Credential {
    credential_id:string;
    name:string;
    description:string;
    credential_type:string;
    grant_type?: string;
    config: {
        token_url: string;
        grant_type: string; // We can remove this field in the future 
        scope: string;
        token_ttl_seconds: number;
        header_name?: string;
        prefix?: string;
        http_method?: string;
        request_body_template?: string;
        response_token_path?: string;
        response_expiry_path?: string;
        headers?: string;
        content_type?: string;
        key?: string;
        value_type?: string;
    }
    secrets?: {
        client_id?: string;
        client_secret?: string;
        username?: string;
        password?: string;
        api_key?: string;
        api_secret?: string;
        token?: string;
        value?: string;
    },
    masked_secrets?: {
        client_id?: string;
        client_secret?: string;
        username?: string;
        password?: string;
        api_key?: string;
        api_secret?: string;
        token?: string;
        value?: string;
    }
    tags?: string[];
    is_active?: boolean;
}

export interface CredentialPayload {
    name: string;
    description: string;
    credential_type: string;
    config?: {
        token_url?: string;
        grant_type?: string; // We can remove this field in the future 
        scope?: string;
        token_ttl_seconds?: number;
        access_cookie_name?: string;
        header_name?: string;
        prefix?: string;
        http_method?: string;
        request_body_template?: string;
        response_token_path?: string;
        response_expiry_path?: string;
        headers?: string;
        content_type?: string;
        key?: string;
    }
    secrets: {
        client_id?: string;
        client_secret?: string;
        username?: string;
        password?: string;
        api_key?: string;
        api_secret?: string;
        token?: string;
        value?: string | Record<string, string>;
    },
    tags?: string[];
    is_active?: boolean;
}

/** Single data connector row from GET /api/v1/data_connectors */
export interface DataConnector {
    connector_id: string;
    connector_name: string;
    connector_type: string;
    description?: string;
    credential_id: string;
    status: string;
    /** e.g. Production | Staging | Development */
    environment?: string;
    created_at?: string;
    updated_at?: string;
    created_by?: string;
    updated_by?: string;
    request_metadata?: Record<string, unknown>;
}

/** Query string for GET /api/v1/data_connectors (e.g. ?status=active&search=ape) */
export interface GetDataConnectorsQueryArg {
  search?: string;
  status?: 'active' | 'inactive';
}

/** `data` object from GET /api/v1/data_connectors list response */
export interface DataConnectorsListPayload {
    connectors: DataConnector[];
    total_connections?: number;
    active_connections?: number;
    inactive_connections?: number;
    total_all_connections?: number;
}

export interface ConnectorRequestMetadata {
    mcp_server_id?: string;
    mcp_server_name?: string;
}

export interface CreateDataConnectorPayload {
    connector_name: string;
    connector_type: string;
    credential_id: string;
    description?: string;
    status?: string;
    environment?: string;
    request_metadata?: ConnectorRequestMetadata;
}

export interface UpdateDataConnectorPayload {
    connector_name?: string;
    connector_type?: string;
    credential_id?: string;
    description?: string;
    status?: string;
    environment?: string;
    request_metadata?: ConnectorRequestMetadata;
}